import * as React from 'react';
import { useState, useEffect, ChangeEvent, useMemo } from 'react';
import { Table, Button, Input, Form, message, Space, Row, Col, Grid, Modal, Select, Upload } from 'antd';
import apiClient from '../services/api';
import { Employee, Company, User } from '../types';
import { EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import { fetchWithLoading } from '../utils/fetchHelper';
import { confirmDelete } from '../utils/confirmDelete';
import { useAuth } from '../hooks/useAuth';
import EmployeeForm from '../components/EmployeeForm';
import * as XLSX from 'xlsx';
import { accentInsensitiveIncludes } from '../utils/accentUtils';

const EmployeePage = () => {
  const [form] = Form.useForm();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<Employee | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [companyMap, setCompanyMap] = useState<Record<number, string>>({});
  const [companyFilter, setCompanyFilter] = useState<number | undefined>(undefined);
  const [modalVisible, setModalVisible] = useState(false);
  const [userList, setUserList] = useState<User[]>([]);
  const [locationList, setLocationList] = useState<any[]>([]); // Thêm state locationList
  const { user, loading: authLoading } = useAuth();

  // useMemo cho columns
  const columns = useMemo(() => [
    { title: 'Tên nhân viên', dataIndex: 'full_name', key: 'full_name' },
    { title: 'Mã NV', dataIndex: 'employee_code', key: 'employee_code' },
    { title: 'Phòng ban', dataIndex: 'department', key: 'department' },
    {
      title: 'Kiểm soát tài sản',
      dataIndex: 'managed_location_ids',
      key: 'managed_location_ids',
      render: (ids: number[]) => {
        if (!ids || ids.length === 0) return '-';
        return ids.map(id => locationList.find(l => l.id === id)?.name || id).join(', ');
      }
    },
    { title: 'Công ty', dataIndex: 'company_id', key: 'company_id', render: (id: number) => companyMap[id] || id },
    {
      title: 'Hành động',
      key: 'action',
      width: 110,
      render: (_: any, record: Employee) => (
        <Space>
          {canEdit(record) && <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />}
          {canDelete(record) && <Button icon={<DeleteOutlined />} size="small" danger onClick={() => handleDeleteClick(record)} />}
        </Space>
      )
    }
  ], [companyMap, employees, locationList]);

  useEffect(() => {
    if (!user) return;
    fetchEmployees();
    fetchCompanies();
    fetchUsers(); // Lấy danh sách user
    fetchLocations(); // Lấy danh sách vị trí
  }, [user]);

  if (authLoading) return null;
  if (!user || (user.role !== 'super_admin' && user.role !== 'company_admin' && user.role !== 'manager')) {
    return <div>Bạn không có quyền truy cập trang này.</div>;
  }

  const fetchEmployees = async () => {
    await fetchWithLoading(
      () => apiClient.getEmployees(),
      setLoading,
      (data) => setEmployees(data as Employee[]),
      'Không thể tải danh sách nhân viên'
    );
  };

  const fetchCompanies = async () => {
    await fetchWithLoading(
      () => apiClient.getCompanies(),
      () => { },
      (companies) => setCompanyMap(Object.fromEntries((companies as Company[]).map((c: Company) => [c.id, c.name]))),
      'Không thể tải danh sách công ty'
    );
  };

  const fetchUsers = async () => {
    try {
      const users = await apiClient.getUsers() as User[];
      setUserList(users);
    } catch (err) {
      setUserList([]);
    }
  };

  const fetchLocations = async () => {
    try {
      const locations = await apiClient.getLocations() as any[];
      setLocationList(locations);
    } catch (err) {
      setLocationList([]);
    }
  };

  const handleCreateClick = () => {
    setEditingEmployee(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record: Employee) => {
    setEditingEmployee(record);
    const formValues: any = {
      full_name: record.full_name,
      employee_code: record.employee_code,
      department: record.department,
      managed_location_ids: record.managed_location_ids || [],
      user_id: record.user_id
    };
    // Chỉ super_admin mới được set company_id trong form
    if (user?.role === 'super_admin') {
      formValues.company_id = record.company_id;
    }
    form.setFieldsValue(formValues);
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await apiClient.deleteEmployee(id);
      message.success('Đã xóa nhân viên');
      fetchEmployees();
    } catch {
      message.error('Xóa nhân viên thất bại!');
    }
  };

  const handleDeleteClick = (record: Employee) => {
    confirmDelete('Bạn có chắc muốn xóa nhân viên này?', () => handleDelete(record.id));
  };

  const handleSubmit = async (values: any) => {
    try {
      const data = {
        ...values,
        managed_location_ids: values.managed_location_ids || [],
        user_id: values.user_id || null,
        company_id: (user?.role === 'company_admin' || user?.role === 'manager') ? user.company_id : Number(values.company_id),
        ...(editingEmployee ? {} : { public_uuid: uuidv4() })
      };
      if (editingEmployee) {
        // Khi cập nhật, company_admin không được thay đổi company_id
        if (user?.role === 'company_admin') {
          delete data.company_id;
        }
        await apiClient.updateEmployee(editingEmployee.id, data);
        message.success('Cập nhật nhân viên thành công!');
      } else {
        await apiClient.createEmployee(data);
        message.success('Tạo nhân viên thành công!');
      }
      form.resetFields();
      setEditingEmployee(null);
      setModalVisible(false);
      fetchEmployees();
    } catch {
      message.error(editingEmployee ? 'Cập nhật thất bại!' : 'Tạo nhân viên thất bại!');
    }
  };

  const filteredEmployees = employees.filter((emp: Employee) =>
    (accentInsensitiveIncludes(emp.full_name, searchValue) ||
      accentInsensitiveIncludes(emp.employee_code || '', searchValue)) &&
    (companyFilter ? emp.company_id === companyFilter : true)
  );

  // Phân quyền thao tác
  const canEdit = (emp: Employee) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    if ((user.role === 'company_admin' || user.role === 'manager') && emp.company_id === user.company_id) return true;
    return false;
  };
  const canDelete = (emp: Employee) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    if (user.role === 'company_admin' && emp.company_id === user.company_id) return true;
    return false;
  };

  // Thêm hàm xử lý import
  const handleImport = async (file: File) => {
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        await apiClient.importEmployees(jsonData);
        message.success('Import thành công!');
        fetchEmployees();
      };
      reader.readAsArrayBuffer(file);
    } catch (err) {
      message.error('Import thất bại!');
    }
    return false;
  };

  // Thêm hàm export file mẫu
  const exportEmployeeTemplate = () => {
    const data = [
      ['Tên nhân viên', 'Mã nhân viên', 'Phòng ban', 'Công ty'],
      ['Nguyễn Văn A', 'EMP001', 'Phòng Kế toán', 'Công ty ABC']
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Employees');
    XLSX.writeFile(workbook, 'employee_import_template.xlsx');
  };

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: 16, alignItems: 'center' }}>
        <Col xs={24} md={user.role === 'super_admin' ? 14 : 20}>
          <Input
            className="ams-input"
            placeholder="Tìm kiếm tên nhân viên"
            value={searchValue}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
            allowClear
            size="large"
            style={{ width: '100%', borderRadius: 8, height: 40 }}
          />
        </Col>
        {user && user.role === 'super_admin' && (
          <Col xs={24} md={6}>
            <Select
              className="ams-input"
              value={companyFilter || ''}
              onChange={(v: string | number) => setCompanyFilter(v ? Number(v) : undefined)}
              size="large"
              allowClear
              style={{ width: '100%', borderRadius: 8, height: 40 }}
              placeholder="Lọc theo công ty"
              dropdownStyle={{ borderRadius: 8 }}
              options={[
                { value: '', label: 'Tất cả công ty' },
                ...Object.entries(companyMap).map(([id, name]) => ({ value: Number(id), label: name }))
              ]}
            />
          </Col>
        )}
        {(user.role === 'super_admin' || user.role === 'company_admin' || user.role === 'manager') && (
          <Col xs={24} md={4}>
            <Button className="ams-btn" type="primary" size="large" icon={<EditOutlined />} style={{ borderRadius: 8, height: 40, width: '100%' }} onClick={handleCreateClick}>Thêm mới</Button>
          </Col>
        )}
      </Row>
      <Modal
        open={modalVisible}
        title={editingEmployee ? 'Cập nhật nhân viên' : 'Tạo mới nhân viên'}
        onCancel={() => { setModalVisible(false); setEditingEmployee(null); form.resetFields(); }}
        footer={null}
        destroyOnHidden
      >
        <EmployeeForm
          form={form}
          initialValues={editingEmployee || {}}
          onFinish={handleSubmit}
          loading={loading}
          submitText={editingEmployee ? 'Cập nhật' : 'Tạo mới'}
          companies={Object.entries(companyMap).map(([id, name]) => ({ id: Number(id), name }))}
          isSuperAdmin={user?.role === 'super_admin'}
          currentUser={user}
          userList={userList.filter(u =>
            !employees.some(e => e.user_id === u.id) ||
            (editingEmployee && editingEmployee.user_id === u.id)
          )}
          locations={locationList}
        />
      </Modal>
      <Table
        dataSource={filteredEmployees}
        rowKey="id"
        loading={loading}
        columns={columns}
        scroll={{ x: 'max-content' }}
        pagination={{ pageSize: 10 }}
        style={{ marginBottom: 16 }}
      />
      {/* Nút Export và Import ở dưới, bên trái bảng - chỉ hiển thị với super_admin */}
      {user?.role === 'super_admin' && (
        <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 8, marginTop: 8 }}>
          <Button onClick={exportEmployeeTemplate} icon={<UploadOutlined />}>Export file mẫu</Button>
          <Upload
            accept=".xlsx,.xls,.csv"
            showUploadList={false}
            beforeUpload={handleImport}
          >
            <Button>Import</Button>
          </Upload>
        </div>
      )}
    </div>
  );
};

export default EmployeePage; 